
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface AdminRouteProps {
  children: React.ReactNode;
}

const AdminRoute = ({ children }: AdminRouteProps) => {
  const { user, isLoading, isAdmin } = useAuth();
  const { toast } = useToast();
  
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [hasAdminAccess, setHasAdminAccess] = useState<boolean>(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    // Create a flag to track if component is still mounted
    let mounted = true;
    
    const checkAdminStatus = async () => {
      if (!user) {
        console.log('No user found, denying admin access');
        if (mounted) {
          setHasAdminAccess(false);
          setCheckingAdmin(false);
          setShouldRedirect(true);
        }
        return;
      }

      try {
        // Check admin status from local storage first
        const cachedAdminStatus = localStorage.getItem('adminStatus');
        const cachedTimestamp = localStorage.getItem('adminStatusTimestamp');
        const now = Date.now();
        
        // Use cached admin status if recent (last 5 minutes)
        if (cachedAdminStatus && cachedTimestamp && 
            (now - parseInt(cachedTimestamp)) < 5 * 60 * 1000) {
          console.log('Using cached admin status:', cachedAdminStatus);
          if (mounted) {
            setHasAdminAccess(cachedAdminStatus === 'true');
            setCheckingAdmin(false);
          }
          return;
        }
        
        // Use isAdmin from context first
        if (isAdmin) {
          console.log('User is already confirmed as admin in context');
          
          // Update cache
          localStorage.setItem('adminStatus', 'true');
          localStorage.setItem('adminStatusTimestamp', now.toString());
          
          if (mounted) {
            setHasAdminAccess(true);
            setCheckingAdmin(false);
          }
          return;
        }

        // Double-check with the RPC function from Supabase as a fallback
        const { data, error } = await supabase.rpc('is_admin');
        
        if (error) {
          console.error('Error checking admin status:', error);
          toast({
            title: "Error checking permissions",
            description: error.message,
            variant: "destructive",
          });
          
          if (mounted) {
            setHasAdminAccess(false);
            setShouldRedirect(true);
          }
        } else {
          console.log('Admin status check result:', data);
          
          // Update cache
          localStorage.setItem('adminStatus', data ? 'true' : 'false');
          localStorage.setItem('adminStatusTimestamp', now.toString());
          
          if (mounted) {
            if (!!data) {
              setHasAdminAccess(true);
            } else {
              setHasAdminAccess(false);
              setShouldRedirect(true);
            }
          }
        }
      } catch (err) {
        console.error('Error checking admin status:', err);
        if (mounted) {
          setHasAdminAccess(false);
          setShouldRedirect(true);
        }
      } finally {
        if (mounted) {
          setCheckingAdmin(false);
        }
      }
    };

    checkAdminStatus();
    
    // Cleanup function to prevent state updates on unmounted component
    return () => {
      mounted = false;
    };
  }, [user, isAdmin, toast]);

  // Show toast messages in useEffect to prevent render loops
  useEffect(() => {
    if (shouldRedirect && !checkingAdmin) {
      if (!user) {
        toast({
          title: "Authentication required",
          description: "You must be logged in to access this page.",
          variant: "destructive",
        });
      } else if (!hasAdminAccess) {
        toast({
          title: "Access denied",
          description: "You do not have permission to access this page.",
          variant: "destructive",
        });
      }
    }
  }, [shouldRedirect, checkingAdmin, user, hasAdminAccess, toast]);

  if (isLoading || checkingAdmin) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <p className="mt-4 text-muted-foreground">Checking permissions...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/sign-in" replace />;
  }

  if (!hasAdminAccess) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default AdminRoute;
